import React from 'react';
import { LucideIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CardHeader, CardTitle, CardContent } from '../ui';

export interface ActionButton {
  id: string;
  label: string;
  icon: LucideIcon;
  variant?: 'default' | 'outline' | 'destructive';
  disabled?: boolean;
  loading?: boolean;
}

interface ToolActionsPanelProps {
  title?: string;
  actions: ActionButton[];
  onActionClick: (actionId: string) => void;
  isProcessing?: boolean;
  className?: string;
}

export const ToolActionsPanel: React.FC<ToolActionsPanelProps> = ({
  title,
  actions,
  onActionClick,
  isProcessing = false,
  className = '',
}) => {
  const { t } = useTranslation();

  // Use translation as default value if title prop is not provided
  const displayTitle = title || t('tools.actions.title');

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg sm:text-xl">{displayTitle}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {actions.map((action) => {
          const Icon = action.icon;
          const isLoading =
            action.loading || (isProcessing && action.id === 'primary');

          return (
            <Button
              key={action.id}
              onClick={() => onActionClick(action.id)}
              disabled={action.disabled || isProcessing}
              variant={action.variant || 'default'}
              className="w-full gap-2 text-sm sm:text-base"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('tools.actions.processing')}
                </>
              ) : (
                <>
                  <Icon className="w-4 h-4" />
                  {action.label}
                </>
              )}
            </Button>
          );
        })}
      </CardContent>
    </Card>
  );
};
